import { joinURL } from 'ufo'
import type { RouteLocationNormalized } from 'vue-router'

/**
 * Recovers from failed JS chunk imports instead of leaving the user on a dead page.
 *
 * After a deploy the previous build's hashed chunks are gone from the server, so any
 * tab that stayed open long enough to navigate afterwards tries to `import()` a file
 * that now 404s. Vite dispatches a cancelable `vite:preloadError` for that and rethrows
 * the error unless something calls `preventDefault()` — which surfaces in Sentry as an
 * unhandled rejection ("Importing a module script failed." in Safari/WebKit, "Failed to
 * fetch dynamically imported module" in Chromium).
 *
 * Nuxt's own recovery (`experimental.emitRouteChunkError: 'automatic'`) only reloads
 * from inside `router.onError`, so it covers a failing *route* component but nothing
 * else — a lazy component or any import outside a route transition just breaks. And the
 * proactive path (`app:manifest:update` → reload on next navigation) is off here because
 * the shared module sets `experimental.checkOutdatedBuildInterval = false`.
 *
 * So: reload the app at the route the user was heading to. `preventDefault()` is only
 * called when we really are reloading — swallowing the rejection without replacing the
 * page would resolve the import to `undefined` and fail in a stranger way further down.
 *
 * Skipped when the browser reports it is offline: the chunk is fine, the connection
 * isn't, and reloading would drop the user on the browser's offline page.
 */

const RELOAD_KEY = 'chunk-error:reloaded-at'

/** Long enough that a reload which didn't help stops looping and reaches Sentry instead. */
const RELOAD_COOLDOWN = 30_000

function reloadedRecently(): boolean {
  try {
    const at = Number(sessionStorage.getItem(RELOAD_KEY))
    return at > 0 && Date.now() - at < RELOAD_COOLDOWN
  } catch {
    return false
  }
}

function markReloaded(): void {
  try {
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()))
  } catch {
    // Storage can be unavailable (private mode, blocked cookies) — without the marker
    // the cooldown is gone, so bail out rather than risk a reload loop.
    throw new Error('sessionStorage unavailable')
  }
}

export default defineNuxtPlugin(() => {
  const router = useRouter()
  const config = useRuntimeConfig()

  // The failing import is usually the chunk of the route being opened, and that
  // navigation never commits — so remember the target to reload straight into it.
  let navigatingTo: RouteLocationNormalized | null = null
  addRouteMiddleware((to) => {
    navigatingTo = to
  })

  window.addEventListener('vite:preloadError', (event) => {
    if (navigator.onLine === false) return
    if (reloadedRecently()) return

    try {
      markReloaded()
    } catch {
      return
    }

    // Cancelling the event makes the failed import resolve to `undefined`, which Vue
    // Router reports as an unresolvable route component until the reload lands. Flag it
    // so Sentry drops that follow-up noise (see app/utils/chunkErrorReload.ts).
    markChunkErrorReloading()
    event.preventDefault()

    const to = navigatingTo ?? router.currentRoute.value
    reloadNuxtApp({ path: joinURL(config.app.baseURL, to.fullPath), force: true })
  })
})
